import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, AlertTriangle, Eraser, FileText, Shield, Lock, Pen, Type } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Iter 40: cursive font choices the signer can pick when typing their name.
// All fonts ship inline via Google Fonts <link> injected on mount so the
// canvas can render them when generating the signature PNG.
const CURSIVE_FONTS = [
  { id: 'Dancing Script', label: 'Dancing Script' },
  { id: 'Great Vibes', label: 'Great Vibes' },
  { id: 'Allura', label: 'Allura' },
  { id: 'Pacifico', label: 'Pacifico' },
  { id: 'Sacramento', label: 'Sacramento' },
];
const CURSIVE_FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Allura&family=Dancing+Script:wght@500;700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap';

const SignDocument = () => {
  const { token } = useParams();
  const sigPadRef = useRef(null);
  const sigContainerRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [acceptance, setAcceptance] = useState(false);
  const [canvasW, setCanvasW] = useState(700);
  // Iter 40: signature mode — 'draw' (canvas) or 'type' (cursive).
  const [sigMode, setSigMode] = useState('draw');
  const [cursiveFont, setCursiveFont] = useState(CURSIVE_FONTS[0].id);
  const cursivePreviewRef = useRef(null);
  // Iter 31: capture browser GPS at signing for audit trail.
  // null = not yet asked; 'pending' = prompt shown; populated = success/denied
  const [geo, setGeo] = useState({ status: 'idle', lat: null, lng: null, accuracy: null });

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeo({ status: 'unsupported', lat: null, lng: null, accuracy: null });
      return;
    }
    setGeo(g => ({ ...g, status: 'pending' }));
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({
        status: 'granted',
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      () => setGeo({ status: 'denied', lat: null, lng: null, accuracy: null }),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  // Iter 40: load Google cursive fonts once so the canvas-rendered typed
  // signature has the chosen handwriting style available.
  useEffect(() => {
    const id = 'cursive-fonts-link';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = CURSIVE_FONT_HREF;
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/compliance/sign/${token}`)
      .then(r => {
        if (!r.ok) throw new Error('Signature request not found or expired.');
        return r.json();
      })
      .then(d => {
        setData(d);
        setSignedName(d?.signature_request?.signer_name || '');
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  // Make signature canvas responsive — measure container width and resize
  useEffect(() => {
    const measure = () => {
      if (sigContainerRef.current) {
        // Account for the 4px (border-2) on each side
        const w = Math.max(280, sigContainerRef.current.clientWidth - 8);
        setCanvasW(w);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [data]);

  const handleClear = () => sigPadRef.current?.clear();

  // Iter 40: render the typed name in the chosen cursive font onto an
  // off-screen canvas and return a PNG data URL. We size the canvas to
  // a comfortable aspect ratio and auto-scale the font so longer names
  // still fit on one line.
  const renderCursiveToPng = (name, fontFamily) => {
    const width = 700;
    const height = 220;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    let fontSize = 96;
    ctx.fillStyle = '#003D5C';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    const measure = (size) => {
      ctx.font = `${size}px "${fontFamily}", cursive`;
      return ctx.measureText(name).width;
    };
    // Shrink font until it fits with 60px horizontal padding.
    while (fontSize > 28 && measure(fontSize) > width - 60) {
      fontSize -= 4;
    }
    ctx.font = `${fontSize}px "${fontFamily}", cursive`;
    ctx.fillText(name, width / 2, height / 2 + 6);
    return canvas.toDataURL('image/png');
  };

  const handleSubmit = async () => {
    if (!signedName.trim()) {
      alert('Please type your full name.');
      return;
    }
    if (sigMode === 'draw' && (!sigPadRef.current || sigPadRef.current.isEmpty())) {
      alert('Please provide your signature.');
      return;
    }
    if (!acceptance) {
      alert('You must accept the terms to sign.');
      return;
    }
    setSubmitting(true);
    try {
      let signature_data;
      if (sigMode === 'type') {
        // Wait one frame so the just-loaded Google font is applied before painting.
        if (document.fonts && document.fonts.ready) {
          try { await document.fonts.ready; } catch (fontErr) { console.debug('Font readiness wait failed (non-blocking):', fontErr); }
        }
        signature_data = renderCursiveToPng(signedName.trim(), cursiveFont);
      } else {
        // Robust fallback: getTrimmedCanvas() depends on trim-canvas lib which can
        // break on minified CJS interop ("uh is not a function"). Use it when
        // available; otherwise capture the full canvas via toDataURL() directly.
        try {
          const pad = sigPadRef.current;
          if (pad && typeof pad.getTrimmedCanvas === 'function') {
            signature_data = pad.getTrimmedCanvas().toDataURL('image/png');
          } else if (pad && typeof pad.toDataURL === 'function') {
            signature_data = pad.toDataURL('image/png');
          } else if (pad && typeof pad.getCanvas === 'function') {
            signature_data = pad.getCanvas().toDataURL('image/png');
          } else {
            throw new Error('Signature pad not initialised. Please refresh and try again.');
          }
        } catch (innerErr) {
          const pad = sigPadRef.current;
          if (pad && typeof pad.getCanvas === 'function') {
            signature_data = pad.getCanvas().toDataURL('image/png');
          } else {
            throw innerErr;
          }
        }
      }
      const res = await fetch(`${API_URL}/api/compliance/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature_data,
          signed_name: signedName,
          acceptance,
          signature_method: sigMode === 'type' ? `typed:${cursiveFont}` : 'drawn',
          geo_lat: geo.lat,
          geo_lng: geo.lng,
          geo_accuracy: geo.accuracy,
          geo_source: geo.status === 'granted' ? 'gps' : geo.status,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit signature');
      }
      setSuccess(true);
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#003D5C]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center" data-testid="sign-error">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Link Not Available</h2>
            <p className="text-gray-600">{error}</p>
            <p className="text-sm text-gray-500 mt-4">Please contact your advisor for a new link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sig = data.signature_request;
  const status = sig?.status;

  if (success || status === 'signed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center" data-testid="sign-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Signature Recorded</h2>
            <p className="text-gray-600 mb-4">Thank you, {sig?.signer_name}. Your electronic signature has been securely recorded.</p>
            <p className="text-sm text-gray-500">A confirmation email has been sent to {sig?.signer_email}.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'expired' || status === 'cancelled') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center" data-testid="sign-error">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Link {status === 'expired' ? 'Expired' : 'Cancelled'}</h2>
            <p className="text-gray-600">This signing link is no longer valid. Please contact your advisor for a new link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const c = data.case;
  const advisor = data.advisor;
  const client = data.client;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#003D5C] text-white py-4 sm:py-6 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-base sm:text-xl font-bold truncate" data-testid="sign-page-title">Utmost Financial Inc.</h1>
            <p className="text-xs sm:text-sm text-[#D4AF37]">Compliance File — Electronic Signature</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs sm:text-sm bg-white/10 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex-shrink-0">
            <Lock className="h-3 w-3 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Secure Link</span><span className="sm:hidden">Secure</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
        {/* Welcome */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <p className="text-gray-700 text-sm sm:text-base">Hello <span className="font-semibold" data-testid="signer-name">{sig.signer_name}</span>,</p>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">Your advisor <strong>{advisor?.name || 'Utmost Advisor'}</strong> has prepared compliance file <strong data-testid="case-number">{c?.case_number}</strong> and is requesting your electronic signature to complete the file.</p>
          </CardContent>
        </Card>

        {/* Case Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4 text-[#0077B6]" />Case Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-sm">
              <div><span className="text-gray-500">Case Number:</span> <span className="font-medium break-all">{c?.case_number}</span></div>
              <div><span className="text-gray-500">Carrier:</span> <span className="font-medium">{c?.carrier}</span></div>
              <div><span className="text-gray-500">Product:</span> <span className="font-medium">{c?.product_type?.replace(/_/g, ' ')}</span></div>
              <div><span className="text-gray-500">Coverage:</span> <span className="font-medium">${(c?.coverage_amount || 0).toLocaleString()}</span></div>
              <div><span className="text-gray-500">Premium:</span> <span className="font-medium">${(c?.premium || 0).toFixed(2)} / {c?.payment_frequency}</span></div>
              {client && <div><span className="text-gray-500">Client:</span> <span className="font-medium">{client.full_name}</span></div>}
            </div>
          </CardContent>
        </Card>

        {/* Document Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4 text-[#0077B6]" />Review Compliance Package</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-3">Please review the auto-populated compliance package before signing.</p>
            <iframe
              src={`${API_URL}/api/compliance/sign/${token}/package`}
              title="Compliance Package"
              className="w-full h-[360px] sm:h-[480px] border rounded-lg"
              data-testid="compliance-pdf-frame"
            />
            <a
              href={`${API_URL}/api/compliance/sign/${token}/package`}
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-3 text-sm text-[#0077B6] hover:underline"
              data-testid="download-pdf-link"
            >
              Open / Download PDF in new tab
            </a>
          </CardContent>
        </Card>

        {/* Sign */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your Electronic Signature</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type your full legal name</label>
              <input
                type="text"
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                placeholder="Full legal name"
                data-testid="signer-typed-name"
              />
            </div>

            {/* Iter 40: choose between drawn signature and typed cursive */}
            <div className="inline-flex p-1 bg-gray-100 rounded-lg" data-testid="signature-mode-toggle">
              <button
                type="button"
                onClick={() => setSigMode('draw')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md inline-flex items-center gap-1.5 transition ${
                  sigMode === 'draw' ? 'bg-white shadow text-[#003D5C]' : 'text-gray-600 hover:text-gray-800'
                }`}
                data-testid="sig-mode-draw"
              >
                <Pen className="h-3.5 w-3.5" /> Draw
              </button>
              <button
                type="button"
                onClick={() => setSigMode('type')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md inline-flex items-center gap-1.5 transition ${
                  sigMode === 'type' ? 'bg-white shadow text-[#003D5C]' : 'text-gray-600 hover:text-gray-800'
                }`}
                data-testid="sig-mode-type"
              >
                <Type className="h-3.5 w-3.5" /> Type (Cursive)
              </button>
            </div>

            {sigMode === 'draw' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Draw your signature below</label>
                <div ref={sigContainerRef} className="border-2 border-dashed border-gray-300 rounded-lg bg-white" style={{ touchAction: 'none' }}>
                  <SignatureCanvas
                    ref={sigPadRef}
                    penColor="#003D5C"
                    canvasProps={{
                      width: canvasW,
                      height: 180,
                      className: 'block',
                      style: { width: '100%', height: 180, touchAction: 'none' },
                      'data-testid': 'signature-canvas',
                    }}
                  />
                </div>
                <Button variant="outline" size="sm" className="mt-2" onClick={handleClear} data-testid="clear-signature-btn">
                  <Eraser className="h-4 w-4 mr-1" /> Clear
                </Button>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Choose a cursive style</label>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="cursive-font-picker">
                  {CURSIVE_FONTS.map(f => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setCursiveFont(f.id)}
                      className={`px-3 py-2 border rounded-md text-base transition ${
                        cursiveFont === f.id
                          ? 'border-[#003D5C] ring-2 ring-[#003D5C]/30 bg-white'
                          : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                      style={{ fontFamily: `'${f.id}', cursive` }}
                      data-testid={`cursive-font-${f.id.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {signedName.trim() || f.label}
                    </button>
                  ))}
                </div>
                <div
                  ref={cursivePreviewRef}
                  className="border-2 border-dashed border-gray-300 rounded-lg bg-white flex items-center justify-center"
                  style={{ height: 180, minHeight: 180 }}
                  data-testid="cursive-signature-preview"
                >
                  <span
                    className="text-[#003D5C] text-center px-4 break-words"
                    style={{
                      fontFamily: `'${cursiveFont}', cursive`,
                      fontSize: 'clamp(2.25rem, 6vw, 4rem)',
                      lineHeight: 1.1,
                    }}
                  >
                    {signedName.trim() || (
                      <span className="text-gray-400 italic" style={{ fontFamily: 'inherit', fontSize: '1rem' }}>
                        Type your full legal name above to preview your cursive signature
                      </span>
                    )}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  By typing your name and choosing a style, you are adopting this as your electronic signature.
                  It has the same legal effect as a handwritten signature.
                </p>
              </div>
            )}

            <label className="flex items-start gap-2 pt-2 border-t">
              <input
                type="checkbox"
                checked={acceptance}
                onChange={(e) => setAcceptance(e.target.checked)}
                className="mt-1 flex-shrink-0"
                data-testid="acceptance-checkbox"
              />
              <span className="text-sm text-gray-700">
                I confirm that I have reviewed the compliance package above, that the information about me is
                accurate, and that this electronic signature has the same legal effect as a handwritten signature.
                I consent to receive related documents electronically.
              </span>
            </label>

            <div className="text-xs text-gray-500 flex items-center gap-2 pt-1" data-testid="geo-status">
              <Shield className="h-3.5 w-3.5" />
              {geo.status === 'pending' && <span>Requesting location for the signature audit trail…</span>}
              {geo.status === 'granted' && (
                <span>
                  Location captured: <span className="font-mono text-gray-700">{geo.lat.toFixed(4)}, {geo.lng.toFixed(4)}</span>
                  {geo.accuracy ? ` (±${Math.round(geo.accuracy)} m)` : ''}
                </span>
              )}
              {geo.status === 'denied' && <span>Location not shared — your IP address and timestamp will still be recorded.</span>}
              {geo.status === 'unsupported' && <span>This device does not support location capture. IP and timestamp will be recorded.</span>}
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C] py-3 text-base font-semibold"
              data-testid="submit-signature-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Signature'}
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-500 pb-6">
          Dominion Lending Centres Utmost Financial · Powered by Utmost Compliance File Builder
        </p>
      </div>
    </div>
  );
};

export default SignDocument;
